import styles from "./CompleteProfileDialog.module.scss";
import { useTranslation } from "../../i18n";
import { useContext, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { AuthContext } from "../../context/AuthContext";
import { useAlert } from "../../context/AlertContext";
import { fetcher } from "../../utils/fetcher";
import { WarningIcon } from "../Icons/Warning";

interface CompleteProfileDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

export const CompleteProfileDialog = ({
  open,
  onClose,
  onComplete,
}: CompleteProfileDialogProps) => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const { setAlert } = useAlert();

  if (!authContext) {
    throw new Error("AuthContext must be used within an AuthProvider");
  }

  const { currentUser, fetchVolunteer } = authContext;

  const [dateOfBirth, setDateOfBirth] = useState<string>(
    (currentUser?.userData as any)?.date_of_birth || ""
  );
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!dateOfBirth) {
      setAlert(
        true,
        "warning",
        t("completeProfile.alerts.missingDate")
      );
      return;
    }

    if (!currentUser?.userId) {
      setAlert(true, "error", t("completeProfile.alerts.notSignedIn"));
      return;
    }

    setLoading(true);

    try {
      const body = {
        date_of_birth: dateOfBirth,
        step_of_registration: 4,
      };

      await fetcher({
        url: `/volunteer/${currentUser.userId}`,
        method: "PUT",
        body: JSON.stringify(body),
      });

      await fetchVolunteer(currentUser.userId);

      setAlert(
        true,
        "success",
        t("completeProfile.alerts.saved")
      );

      if (onComplete) {
        onComplete();
      }

      onClose();
    } catch (error) {
      console.error("Error updating volunteer profile:", error);
      setAlert(
        true,
        "error",
        t("completeProfile.alerts.failed")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="complete-profile-dialog-title"
      aria-describedby="complete-profile-dialog-description"
      maxWidth="sm"
      fullWidth
      className={styles.dialog}
    >
      <DialogTitle
        id="complete-profile-dialog-title"
        className={styles.dialogTitle}
      >
        <WarningIcon />
        {t("completeProfile.title")}
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <DialogContentText
          id="complete-profile-dialog-description"
          className={styles.description}
        >
          {t("completeProfile.text")}
        </DialogContentText>
        <div className={styles.form}>
          <TextField
            id="date_of_birth"
            type="date"
            label={t("completeProfile.dateOfBirth")}
            variant="outlined"
            size="small"
            fullWidth
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            required
          />
        </div>
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !dateOfBirth}
        >
          {loading ? t("completeProfile.saving") : t("completeProfile.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

